import { CommonModule, CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseCardComponent } from '@fuse/components/card';
import { PermissionService } from 'app/components/permission-modal/permission.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { AdminCategoryMetricsDTO } from '../../categories/list-categories/category';
import { CategoryService } from '../../categories/list-categories/category.service';
import { AdminProductMetricsDTO, AdminProductSearchDTO } from './product';
import { ProductService } from './product.service';
import { AnnouncementService } from 'app/modules/announcement/announcement/announcement.service';

@Component({
    selector: 'list-products',
    templateUrl: './list-products.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        FuseCardComponent,
        PercentPipe,
        DatePipe,
        CurrencyPipe,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        MatSortModule
    ],
    providers: [ProductService]
})
export class ListProductsComponent implements OnInit, OnDestroy {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    dataSource = new MatTableDataSource<AdminProductMetricsDTO>([]);
    displayedColumns: string[] = [
        'name',
        'category',
        'price',
        'sellerBadge',
        'views',
        'chatsStarted',
        'createdAt',
        'status',
        'action'
    ];

    searchParams: AdminProductSearchDTO = {
        q: '',
        status: undefined,
        categories: [],
        page: 0,
        size: 10,
        sort: 'createdAt',
        direction: 'DESC'
    };
    totalAmount = 0;

    categories: AdminCategoryMetricsDTO[] = [];

    private _searchSubject = new Subject<string>();
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _productService: ProductService,
        private _announcementService: AnnouncementService, // Injected existing service
        private permissionService: PermissionService,
        private _categoryService: CategoryService
    ) { }

    ngOnInit(): void {
        this.loadProducts();
        this.loadCategories();

        this._searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this._unsubscribeAll)
        ).subscribe((query) => {
            this.searchParams.q = query;
            this.searchParams.page = 0;
            this.loadProducts();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    loadCategories(): void {
        this._categoryService.search({ page: 0, size: 100, sort: 'name', direction: 'ASC' }).subscribe((response) => {
            this.categories = response.list;
            this._changeDetectorRef.detectChanges();
        });
    }

    loadProducts(): void {
        this._productService.search(this.searchParams).subscribe((response) => {
            this.dataSource.data = response.list;
            this.totalAmount = response.totalAmount;
            this._changeDetectorRef.detectChanges();
        });
    }

    onSearch(query: string): void {
        this._searchSubject.next(query);
    }

    onStatusChange(status: any): void {
        this.searchParams.status = status === 'ALL' ? undefined : status;
        this.searchParams.page = 0;
        this.loadProducts();
    }

    onCategoriesChange(event: any): void {
        this.searchParams.categories = event.value;
        this.searchParams.page = 0;
        this.loadProducts();
    }

    clearFilters(): void {
        this.searchParams.q = '';
        this.searchParams.status = undefined;
        this.searchParams.categories = [];
        this.searchParams.page = 0;
        this.loadProducts();
    }

    handlePageEvent(e: PageEvent): void {
        this.searchParams.page = e.pageIndex;
        this.searchParams.size = e.pageSize;
        this.loadProducts();
    }

    onSortChange(sort: Sort): void {
        this.searchParams.sort = sort.active;
        this.searchParams.direction = sort.direction.toUpperCase() as 'ASC' | 'DESC' || 'DESC';
        this.searchParams.page = 0;
        this.loadProducts();
    }

    toggleStatus(product: AdminProductMetricsDTO): void {
        const newStatus = product.status === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED';
        const actionText = newStatus === 'BLOCKED' ? 'Bloquear' : 'Ativar';
        const confirmText = newStatus === 'BLOCKED' ? 'bloquear' : 'ativar';

        Swal.fire({
            title: `${actionText} Produto`,
            text: `Tem certeza que deseja ${confirmText} o produto "${product.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `Sim, ${confirmText}`,
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                const request$ =
                    newStatus === 'BLOCKED'
                        ? this._announcementService.reject({
                            id: product.id,
                            reason: 'Produto rejeitado pelos administradores por não se enquadrar nos requisitos de publicação',
                            status: 'BLOCKED',
                        })
                        : this._productService.updateStatus(
                            product.id,
                            newStatus
                        );

                request$.subscribe(() => {
                    Swal.fire({
                        icon: 'success',
                        title: `Produto ${newStatus === 'BLOCKED' ? 'bloqueado' : 'ativado'} com sucesso!`,
                        showConfirmButton: false,
                        timer: 1500
                    });
                    this.loadProducts();
                });
            }
        });
    }
}
